/**
 * StratIQX Main Footer Component
 * Unified footer system with configurable variants
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { useAuthStore } from '../../stores/authStore'
import { ServiceConfigUtils } from '../../config/services.utils'
import { FooterConfig, NAVIGATION_ACTIONS } from '../../config/navigation.config'
import { cn } from '../../lib/utils'

interface MainFooterProps {
  className?: string
  variant?: 'full' | 'minimal' | 'legal'
}

export const MainFooter: React.FC<MainFooterProps> = ({ 
  className = '', 
  variant = 'full' 
}) => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const footerConfig = FooterConfig.getFooterConfig(variant)
  const { companyInfo, sections, legalLinks, showSections, showContact, showDescription } = footerConfig

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  // Get dynamic services for footer
  const getServicesForFooter = () => {
    const activeServices = ServiceConfigUtils.getActiveServices()
    const comingSoonServices = ServiceConfigUtils.getComingSoonServices()
    
    return [
      ...activeServices.map(service => ({
        id: service.id,
        label: service.name,
        description: service.description
      })),
      ...comingSoonServices.map(service => ({
        id: service.id,
        label: `${service.name} (Coming Soon)`,
        description: service.description
      }))
    ]
  }

  // Populate services section with dynamic content
  const getFooterSections = () => {
    return sections.map(section => {
      if (section.id === 'services') {
        return {
          ...section,
          items: getServicesForFooter()
        }
      }
      return section
    })
  }

  if (variant === 'minimal' || variant === 'legal') {
    return (
      <footer className={cn(
        'py-8 px-4 bg-slate-900 border-t border-blue-800/30',
        className
      )}>
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Company Info */}
            <div className="flex items-center mb-4 md:mb-0">
              <button 
                onClick={() => handleNavigate('/')}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <div className="text-xl font-bold text-white">
                  Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
                </div>
                <div className="ml-3 text-sm text-gray-300 font-medium">
                  {companyInfo.tagline}
                </div>
              </button>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap gap-6 text-sm">
              {legalLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavigate(link.path)}
                  className="text-gray-400 hover:text-cyan-400 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-slate-700 pt-6 mt-6">
            <p className="text-gray-400 text-sm text-center">
              {companyInfo.copyright}
            </p>
          </div>
        </div>
      </footer>
    )
  }

  // Full footer variant
  return (
    <footer id="expertise" className={cn(
      'py-16 px-4 bg-slate-900 border-t border-blue-800/30',
      className
    )}>
      <div className="container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info Section */}
          <div>
            <div className="flex items-center mb-4">
              <button 
                onClick={() => handleNavigate('/')}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <div className="text-xl font-bold text-white">
                  Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
                </div>
              </button>
            </div>
            {showDescription && (
              <>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  {companyInfo.description}
                </p>
                <p className="text-gray-300 text-sm">
                  Ready to amplify your strategic intelligence?
                </p>
              </>
            )}
          </div>

          {/* Dynamic Footer Sections */}
          {showSections && getFooterSections().map((section) => (
            <div key={section.id}>
              <h3 className="text-lg font-semibold text-cyan-400 mb-4">
                {section.title}
              </h3>
              
              {section.id === 'contact' ? (
                // Special handling for contact section
                <div className="space-y-2 text-sm text-gray-300 mb-4">
                  {section.items.map((item) => (
                    <p key={item.id}>{item.label}</p>
                  ))}
                </div>
              ) : (
                // Regular section items
                <ul className="space-y-2 text-sm">
                  {section.items.map((item) => (
                    <li 
                      key={item.id}
                      className={cn(
                        "text-gray-300 transition-colors",
                        {
                          "hover:text-cyan-400 cursor-pointer": item.path || section.id === 'services'
                        }
                      )}
                      onClick={item.path ? () => handleNavigate(item.path!) : undefined}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              )}

              {/* CTA Button for Contact Section */}
              {section.id === 'contact' && showContact && (
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 shadow-lg mt-4"
                >
                  {isAuthenticated ? 'Continue Setup' : 'Get Started Free'}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Legal Links and Copyright */}
        <div className="border-t border-slate-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              {companyInfo.copyright}
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              {legalLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavigate(link.path)}
                  className="text-gray-400 hover:text-cyan-400 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Consultant Link */}
          <div className="mt-8 pt-8 border-t border-slate-700/50 text-center">
            <p className="text-sm text-gray-400">
              Are you a consulting agency?{' '}
              <button
                onClick={() => handleNavigate('/for-consultants')}
                className="text-cyan-400 hover:text-cyan-300 transition-colors underline"
              >
                Learn how firms use StratIQX for client engagements →
              </button>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default MainFooter